/**
 * Who is signed in, read out of the access token already in the store.
 *
 * The token carries the name as a claim, so there is nothing to fetch -
 * and no dependency either: decoding one is a base64url decode of the
 * middle segment. Nothing here verifies the signature, and nothing
 * should be trusted from it beyond what to print; the server is what
 * decides whether a token is any good.
 */

/** How much of a name the header shows before cutting it short. */
export const MAX_DISPLAY_NAME_LENGTH = 14;

const FALLBACK_NAME = 'Account';

export const decodeJwtPayload = (token?: string | null): any | null => {
  if (!token) {
    return null;
  }
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }
  try {
    // base64url -> base64, then pad back to a multiple of four.
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    // decodeURIComponent/escape round-trip so a name with accents or
    // anything outside latin-1 survives atob, which is byte-oriented.
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    // A malformed token is not worth breaking the header over - the
    // request that uses it will fail with a real message soon enough.
    return null;
  }
};

/**
 * Whether the token says its holder administers the library.
 *
 * The only way the frontend can know. There is no endpoint that answers
 * it, so anything the header shows or hides is decided from here - and
 * Add Media is the case that matters, since offering it to somebody who
 * cannot use it means every step of that wizard fails on a 403.
 *
 * Absent counts as no. A session that signed in before the claim existed
 * still holds a valid token without it, and the safe way round is to
 * hide the way in rather than show one that does not work. Signing in
 * again brings it back.
 */
export const isAdminToken = (token?: string | null): boolean =>
  decodeJwtPayload(token)?.is_superuser === true;

/**
 * The name to show for a token.
 *
 * Falls back through email to a generic label, because a session that
 * signed in before the username claim existed still has a valid token
 * and must not end up with a blank button.
 */
export const displayNameFromToken = (token?: string | null): string => {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return FALLBACK_NAME;
  }
  const username = typeof payload.username === 'string' && payload.username;
  if (username) {
    return username;
  }
  const email = typeof payload.email === 'string' && payload.email;
  if (email) {
    return email.split('@')[0] || FALLBACK_NAME;
  }
  return FALLBACK_NAME;
};

/**
 * Shortens a name so it cannot push the rest of the nav around. The
 * header's links already shrink at several breakpoints, and a long
 * name would squeeze them further.
 */
export const truncateName = (
  name: string,
  max = MAX_DISPLAY_NAME_LENGTH,
): string => {
  if (name.length <= max) {
    return name;
  }
  // The ellipsis counts towards the limit, so the result is never
  // wider than asked for.
  return `${name.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
};
